import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

import { ReceiptView } from '../receipt/receipt-view';
import { PaymentService, ReceiptData } from '../../../services/payment';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';
import { DropdownItem } from '../../../services/dropdown';

type LookupKey = 'UID' | 'ID';

/**
 * Find & Download Receipt — look up any receipt by Receipt No (= paymentUniqueId) or Payment Id,
 * then view / print / download it. Search is button-driven; supports a ?ref= deep link.
 */
@Component({
  selector: 'app-receipt-finder',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, ButtonModule, SelectModule, InputTextModule, ReceiptView],
  templateUrl: './receipt-finder.html',
  styleUrls: ['./receipt-finder.css'],
})
export class ReceiptFinder implements OnInit {
  private payment = inject(PaymentService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  readonly keyOptions: DropdownItem[] = [
    { value: 'UID', label: 'Receipt No' },
    { value: 'ID', label: 'Payment Id' },
  ];

  key = signal<LookupKey>('UID');
  term = signal('');
  searching = signal(false);
  searched = signal(false); // true once the first search has run (drives empty vs. prompt state)
  result = signal<ReceiptData | null>(null);

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    // Deep link: /payments/receipts?ref=RCPT-...  (numeric ref → Payment Id, else Receipt No).
    // Subscribe (not snapshot) so a NEW spotlight pick re-runs the lookup even while this
    // page is already open (Angular reuses the component, so ngOnInit fires only once).
    this.route.queryParamMap.subscribe((params) => {
      const ref = (params.get('ref') || '').trim();
      if (!ref) return;
      const numeric = /^\d+$/.test(ref);
      this.key.set(numeric ? 'ID' : 'UID');
      this.term.set(ref);
      this.find();
    });
  }

  find() {
    const value = this.term().trim();
    if (!value) {
      this.toast.warning('Enter a receipt no or payment id to search.');
      return;
    }

    this.searching.set(true);
    this.searched.set(true);
    this.result.set(null);

    const onResult = (res: any) => {
      this.searching.set(false);
      if (res?.success && res?.data) {
        this.result.set(res.data as ReceiptData);
      } else {
        this.result.set(null);
        this.toast.error(res?.message || 'No receipt found for that reference.');
      }
    };
    const onError = (e: any) => {
      this.searching.set(false);
      this.result.set(null);
      this.toast.error(e?.error?.message || 'No receipt found for that reference.');
    };

    if (this.key() === 'ID') {
      const id = Number(value);
      if (!Number.isInteger(id) || id <= 0) {
        this.searching.set(false);
        this.toast.warning('Enter a valid Payment Id.');
        return;
      }
      this.payment.rcData(id).subscribe({ next: onResult, error: onError });
    } else {
      this.payment.rcDataByUid(value).subscribe({ next: onResult, error: onError });
    }
  }

  clear() {
    this.term.set('');
    this.result.set(null);
    this.searched.set(false);
  }

  get placeholder(): string {
    return this.key() === 'ID' ? 'e.g. 55' : 'e.g. RCPT-2026-000123';
  }
}
